import t from "../i18n";

const MOBILE_SESSION_KEY = "acvire.mobile.pendingCall";
const MOBILE_OAUTH_PROVIDERS = ["google_oauth2", "microsoft_graph", "apple"];
const MOBILE_DEVICE_AUTH_PROMPT_DISMISSED_KEY = "acvire.mobile.deviceAuthPromptDismissedAt";
const MOBILE_DEVICE_AUTH_PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const MOBILE_DEVICE_AUTH_BACKGROUND_LOCK_MS = 10 * 1000;
const MOBILE_PIN_HASH_KEY = "acvire.mobile.pinHash";
const MOBILE_PIN_SALT_KEY = "acvire.mobile.pinSalt";
let mobileDeviceAuthUnlockedAt = 0;
let mobileDeviceAuthBackgroundedAt = 0;
let mobileDeviceAuthAuthenticating = false;
let mobileDeviceAuthSessionUnlocked = false;
let mobilePinSessionUnlocked = false;

function csrfToken() {
  const tag = document.querySelector("meta[name='csrf-token']");
  return tag && tag.content;
}

function csrfHeaders(extra = {}) {
  const token = csrfToken();
  return Object.assign(
    {
      "Accept": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    },
    token ? { "X-CSRF-Token": token } : {},
    extra
  );
}

function mobilePlatform() {
  if (window.Capacitor && typeof window.Capacitor.getPlatform === "function") {
    return window.Capacitor.getPlatform();
  }
  return "web";
}

function postJson(url, payload = {}) {
  return fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: csrfHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload)
  });
}

function logPermission(permission, event) {
  return postJson("/mobile/permission_events", {
    permission_event: {
      permission,
      event,
      platform: mobilePlatform()
    }
  }).catch(() => {});
}

function initializeNativeBridge() {
  const plugins = window.Capacitor && window.Capacitor.Plugins;
  if (!plugins) return;

  const push = plugins.PushNotifications;
  if (push && !window.__acvirePushBound) {
    window.__acvirePushBound = true;
    push.requestPermissions().then((result) => {
      logPermission("push_notifications", result.receive === "granted" ? "granted" : "denied");
      if (result.receive === "granted") push.register();
    }).catch(() => logPermission("push_notifications", "failed"));

    push.addListener("registration", (token) => {
      if (!token || !token.value) return;
      const versionMeta = document.querySelector("meta[name='acvire-app-version']");

      postJson("/mobile/device_tokens", {
        device_token: {
          token: token.value,
          platform: mobilePlatform(),
          app_version: versionMeta ? versionMeta.content : "",
          locale: document.documentElement.lang || ""
        }
      }).catch(() => {});
    });
  }

  const app = plugins.App;
  if (app && !window.__acvireDeepLinksBound) {
    window.__acvireDeepLinksBound = true;
    app.addListener("appUrlOpen", (event) => {
      if (!event || !event.url) return;
      if (handleMobileOauthCallback(event.url)) return;

      postJson("/mobile/deep_links/resolve", { target: event.url })
        .then((response) => response.json().then((body) => ({ response, body })))
        .then(({ response, body }) => {
          if (response.ok && body.allowed && body.path) {
            const path = safeInternalPath(body.path);
            if (!path) return;

            if (window.Turbolinks) {
              window.Turbolinks.visit(path);
            } else {
              window.location.assign(path);
            }
          }
        })
        .catch(() => {});
    });

    if (typeof app.getLaunchUrl === "function") {
      app.getLaunchUrl()
        .then((event) => {
          if (!event || !event.url) return;
          if (handleMobileOauthCallback(event.url)) return;

          postJson("/mobile/deep_links/resolve", { target: event.url })
            .then((response) => response.json().then((body) => ({ response, body })))
            .then(({ response, body }) => {
              if (response.ok && body.allowed && body.path) {
                navigateToPath(body.path);
              }
            })
            .catch(() => {});
        })
        .catch(() => {});
    }
  }
}

function initializeMobileOauth() {
  if (!isNativeMobileApp()) return;

  document.querySelectorAll("[data-mobile-oauth-provider]").forEach((link) => {
    if (link.dataset.mobileOauthBound === "true") return;
    link.dataset.mobileOauthBound = "true";
    link.addEventListener("click", (event) => {
      const provider = link.dataset.mobileOauthProvider;
      if (!provider) return;

      event.preventDefault();
      openExternalAuth(provider, link.href);
    });
  });

  MOBILE_OAUTH_PROVIDERS.forEach((provider) => {
    document.querySelectorAll(`form[action$="/users/auth/${provider}"], form[action*="/users/auth/${provider}?"]`).forEach((form) => {
      if (form.dataset.mobileOauthBound === "true") return;
      form.dataset.mobileOauthBound = "true";
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        openExternalAuth(provider);
      });
    });
  });
}

function isNativeMobileApp() {
  return mobilePlatform() !== "web";
}

function openExternalAuth(provider, explicitUrl) {
  const returnTo = safeInternalPath(window.location.pathname + window.location.search) || "/tasks";

  if (isSignedInNativeMobileSession()) {
    navigateToPath(nativeSignedInFallbackPath(returnTo));
    return;
  }

  const startUrl = explicitUrl ? new URL(explicitUrl, window.location.origin) : new URL(`/mobile/oauth/${provider}`, window.location.origin);
  normalizeLoopbackOauthHost(startUrl);
  if (!startUrl.searchParams.get("return_to")) startUrl.searchParams.set("return_to", returnTo);

  const plugins = window.Capacitor && window.Capacitor.Plugins;
  const browser = plugins && plugins.Browser;
  if (browser && typeof browser.open === "function") {
    browser.open({ url: startUrl.toString() });
    return;
  }

  window.open(startUrl.toString(), "_blank", "noopener");
}

function normalizeLoopbackOauthHost(url) {
  if (url.protocol !== "http:") return;
  if (url.hostname === "127.0.0.1" || url.hostname === "::1") {
    url.hostname = "localhost";
  }
}

function initializeMobileAuthTabs() {
  const tabs = Array.from(document.querySelectorAll("[data-mobile-auth-tab]"));
  if (!tabs.length) return;

  const panes = Array.from(document.querySelectorAll("[data-mobile-auth-pane]"));
  tabs.forEach((tab) => {
    if (tab.dataset.mobileAuthBound === "true") return;
    tab.dataset.mobileAuthBound = "true";
    tab.addEventListener("click", () => {
      const target = tab.dataset.mobileAuthTab;

      tabs.forEach((item) => item.classList.toggle("is-active", item === tab));
      panes.forEach((pane) => {
        pane.classList.toggle("is-active", pane.dataset.mobileAuthPane === target);
      });
    });
  });
}

function handleMobileOauthCallback(rawUrl) {
  let url;
  try {
    url = new URL(rawUrl);
  } catch (_error) {
    return false;
  }

  if (url.protocol !== "acvire:" || url.hostname !== "oauth" || url.pathname !== "/callback") {
    return false;
  }

  closeNativeBrowser();

  const error = url.searchParams.get("error");
  if (error) {
    navigateToPath(safeInternalPath(url.searchParams.get("path")) || "/users/sign_in");
    return true;
  }

  const handoff = url.searchParams.get("handoff");
  if (!handoff) return true;

  postJson("/mobile/oauth/exchange", {
    handoff,
    platform: mobilePlatform()
  })
    .then((response) => response.json().then((body) => ({ response, body })))
    .then(({ response, body }) => {
      if (response.ok && body.ok) {
        navigateToPath(safeInternalPath(body.path) || "/tasks");
      } else {
        navigateToPath("/users/sign_in");
      }
    })
    .catch(() => navigateToPath("/users/sign_in"));

  return true;
}

function closeNativeBrowser() {
  const plugins = window.Capacitor && window.Capacitor.Plugins;
  const browser = plugins && plugins.Browser;
  if (browser && typeof browser.close === "function") {
    browser.close().catch(() => {});
  }
}

function navigateToPath(path) {
  const safePath = safeInternalPath(path) || "/tasks";

  if (window.Turbolinks) {
    window.Turbolinks.visit(safePath);
  } else {
    window.location.assign(safePath);
  }
}

function initializeNativeSignedInAuthGuards() {
  if (!isSignedInNativeMobileSession()) return;

  document.querySelectorAll("a[href]").forEach((link) => {
    if (link.dataset.nativeSignedInGuardBound === "true") return;

    const targetPath = sameOriginPath(link.href);
    if (!targetPath || !isMobileAuthShellPath(targetPath)) return;

    link.dataset.nativeSignedInGuardBound = "true";
    link.addEventListener("click", (event) => {
      event.preventDefault();
      navigateToPath(nativeSignedInFallbackPath(link.dataset.returnTo));
    });
  });

  document.querySelectorAll("form[action]").forEach((form) => {
    if (form.dataset.nativeSignedInGuardBound === "true") return;

    const targetPath = sameOriginPath(form.action);
    if (!targetPath || !isMobileAuthShellPath(targetPath)) return;

    form.dataset.nativeSignedInGuardBound = "true";
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      navigateToPath(nativeSignedInFallbackPath(form.dataset.returnTo));
    });
  });
}

function sameOriginPath(rawUrl) {
  try {
    const url = new URL(rawUrl, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (_error) {
    return null;
  }
}

function nativeSignedInFallbackPath(explicitPath) {
  const explicit = safeInternalPath(explicitPath);
  if (explicit && !isMobileAuthShellPath(explicit)) return explicit;

  const current = safeInternalPath(window.location.pathname + window.location.search + window.location.hash);
  if (current && !isMobileAuthShellPath(current)) return current;

  return "/tasks?mobile_app=1";
}

function isMobileAuthShellPath(path) {
  const safePath = safeInternalPath(path);
  if (!safePath) return false;

  return /^\/(?:(?:en|es|fr)\/)?(?:users\/(?:sign_in|sign_up|password|confirmation|unlock|two_factor)|mobile(?:\/app|\/oauth)?)(?:[/?#]|$)/.test(safePath);
}

function initializeNearbyLeads() {
  const root = document.querySelector("[data-mobile-field-sales]");
  if (!root) return;

  const button = root.querySelector("[data-mobile-nearby-load]");
  const status = root.querySelector("[data-mobile-nearby-status]");
  const results = root.querySelector("[data-mobile-nearby-results]");
  const mapNode = root.querySelector("[data-mobile-nearby-map]");
  if (!button || button.dataset.bound === "true") return;
  button.dataset.bound = "true";

  button.addEventListener("click", () => {
    status.textContent = t("mobile.nearby.location_requested", {}, "Standort wird abgefragt ...");
    logPermission("foreground_location", "requested");
    requestForegroundPosition(
      (latitude, longitude) => loadNearbyLeads(root, latitude, longitude, status, results, mapNode),
      () => {
        status.textContent = t("mobile.nearby.location_denied", {}, "Standortfreigabe wurde abgelehnt oder konnte nicht gelesen werden.");
      }
    );
  });
}

function requestForegroundPosition(onSuccess, onFailure) {
  const plugins = window.Capacitor && window.Capacitor.Plugins;
  const geolocation = plugins && plugins.Geolocation;

  if (geolocation) {
    geolocation.requestPermissions()
      .then(() => geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 60000
      }))
      .then((position) => {
        logPermission("foreground_location", "granted");
        onSuccess(position.coords.latitude, position.coords.longitude);
      })
      .catch(() => {
        logPermission("foreground_location", "denied");
        onFailure();
      });
    return;
  }

  if (!navigator.geolocation) {
    logPermission("foreground_location", "unavailable");
    onFailure();
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      logPermission("foreground_location", "granted");
      onSuccess(position.coords.latitude, position.coords.longitude);
    },
    () => {
      logPermission("foreground_location", "denied");
      onFailure();
    },
    { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
  );
}

function loadNearbyLeads(root, latitude, longitude, status, results, mapNode) {
  const projectInput = root.querySelector("[data-mobile-nearby-project]");
  const radiusInput = root.querySelector("[data-mobile-nearby-radius]");
  const projectId = projectInput ? projectInput.value : "";
  const radiusKm = radiusInput ? radiusInput.value : "10";
  const params = new URLSearchParams({ lat: latitude, lng: longitude, radius_km: radiusKm });
  if (projectId) params.set("project_id", projectId);

  status.textContent = t("mobile.nearby.loading", {}, "Leads werden geladen ...");
  fetch(`/mobile/nearby_leads?${params.toString()}`, {
    credentials: "same-origin",
    headers: csrfHeaders()
  })
    .then((response) => response.json().then((body) => ({ response, body })))
    .then(({ response, body }) => {
      if (!response.ok) throw new Error(body.error || "Nearby lookup failed");
      status.textContent = t("mobile.nearby.count_in_radius", { count: body.count, radius: body.radius_km }, `${body.count} Leads im Radius von ${body.radius_km} km.`);
      renderNearbyResults(results, body.leads || []);
      renderNearbyMap(mapNode, latitude, longitude, body.leads || []);
    })
    .catch(() => {
      status.textContent = t("mobile.nearby.load_failed", {}, "Leads in der Nähe konnten nicht geladen werden.");
    });
}

function renderNearbyResults(node, leads) {
  if (!node) return;
  node.replaceChildren();

  if (!leads.length) {
    node.appendChild(emptyNearbyState());
    return;
  }

  leads.forEach((lead) => node.appendChild(nearbyLeadCard(lead)));

  bindCallActions(node);
  bindContactImport(node);
}

function renderNearbyMap(node, latitude, longitude, leads) {
  if (!node || !window.maplibregl) return;
  node.hidden = false;
  node.replaceChildren();

  const map = new window.maplibregl.Map({
    container: node,
    style: "https://demotiles.maplibre.org/style.json",
    center: [longitude, latitude],
    zoom: 11
  });

  new window.maplibregl.Marker({ color: "#004F6E" })
    .setLngLat([longitude, latitude])
    .setPopup(new window.maplibregl.Popup().setText(t("mobile.nearby.your_location", {}, "Dein Standort")))
    .addTo(map);

  leads.forEach((lead) => {
    new window.maplibregl.Marker({ color: "#f97316" })
      .setLngLat([lead.longitude, lead.latitude])
      .setPopup(new window.maplibregl.Popup().setText(lead.display_name))
      .addTo(map);
  });
}

function bindCallActions(scope = document) {
  scope.querySelectorAll("[data-mobile-call-start]").forEach((link) => {
    if (link.dataset.callBound === "true") return;
    link.dataset.callBound = "true";
    link.addEventListener("click", () => {
      logPermission("phone", "opened");
      sessionStorage.setItem(MOBILE_SESSION_KEY, JSON.stringify({
        leadId: link.dataset.leadId,
        startedAt: new Date().toISOString()
      }));
    });
  });
}

function bindContactImport(scope = document) {
  scope.querySelectorAll("[data-mobile-contact-import]").forEach((button) => {
    if (button.dataset.contactBound === "true") return;
    button.dataset.contactBound = "true";
    button.addEventListener("click", () => importSelectedContact(button.dataset.leadId));
  });
}

function importSelectedContact(leadId) {
  if (!navigator.contacts || !navigator.contacts.select) {
    logPermission("contacts", "unavailable");
    return;
  }

  logPermission("contacts", "requested");
  navigator.contacts.select(["name", "email", "tel"], { multiple: false })
    .then((contacts) => {
      const contact = contacts && contacts[0];
      if (!contact) return;
      logPermission("contacts", "granted");
      return postJson("/mobile/contact_imports", {
        contact: {
          lead_id: leadId,
          full_name: Array.isArray(contact.name) ? contact.name[0] : contact.name,
          email: Array.isArray(contact.email) ? contact.email[0] : contact.email,
          tel: Array.isArray(contact.tel) ? contact.tel[0] : contact.tel
        }
      });
    })
    .catch(() => logPermission("contacts", "denied"));
}

function maybeShowCallLogPrompt() {
  const pendingRaw = sessionStorage.getItem(MOBILE_SESSION_KEY);
  if (!pendingRaw) return;

  let pending;
  try {
    pending = JSON.parse(pendingRaw);
  } catch (_error) {
    sessionStorage.removeItem(MOBILE_SESSION_KEY);
    return;
  }

  const startedAt = new Date(pending.startedAt);
  if (!pending.leadId || Number.isNaN(startedAt.getTime())) return;
  if (Date.now() - startedAt.getTime() < 8000) return;

  const modalNode = document.querySelector("[data-mobile-call-log-modal]");
  if (!modalNode || !window.bootstrap) return;

  modalNode.querySelector("[data-mobile-call-log-lead-id]").value = pending.leadId;
  modalNode.querySelector("[data-mobile-call-log-started-at]").value = pending.startedAt;
  modalNode.querySelector("[data-mobile-call-log-ended-at]").value = new Date().toISOString();
  modalNode.querySelector("[data-mobile-call-log-lead]").textContent = leadLabelFor(pending.leadId);

  window.bootstrap.Modal.getOrCreateInstance(modalNode).show();
}

function initializeCallLogForm() {
  const form = document.querySelector("[data-mobile-call-log-form]");
  if (!form || form.dataset.bound === "true") return;
  form.dataset.bound = "true";
  form.addEventListener("ajax:success", () => {
    sessionStorage.removeItem(MOBILE_SESSION_KEY);
    window.bootstrap.Modal.getOrCreateInstance(document.querySelector("[data-mobile-call-log-modal]")).hide();
  });
  form.addEventListener("submit", () => {
    sessionStorage.removeItem(MOBILE_SESSION_KEY);
  });
}

function initializeAutomationJsonActions() {
  document.querySelectorAll("form[data-mobile-automation-action]").forEach((form) => {
    if (form.dataset.mobileAutomationBound === "true") return;
    form.dataset.mobileAutomationBound = "true";
    form.addEventListener("ajax:success", () => {
      if (window.Turbolinks) window.Turbolinks.visit(window.location.href);
    });
  });
}

function emptyNearbyState() {
  const wrapper = document.createElement("div");
  wrapper.className = "task-empty-state task-empty-state--compact";

  const title = document.createElement("strong");
  title.textContent = t("mobile.nearby.none_found", {}, "Keine Leads gefunden");
  wrapper.appendChild(title);

  const description = document.createElement("span");
  description.textContent = t("mobile.nearby.empty_hint", {}, "Erhöhe den Radius oder wähle ein anderes Projekt.");
  wrapper.appendChild(description);

  return wrapper;
}

function nearbyLeadCard(lead) {
  const article = document.createElement("article");
  article.className = "mobile-nearby-lead";

  const body = document.createElement("a");
  body.className = "mobile-nearby-lead__body";
  body.href = safeInternalPath(lead.url) || "#";

  const title = document.createElement("strong");
  title.textContent = lead.display_name || `Lead #${lead.id}`;
  body.appendChild(title);

  const subtitle = document.createElement("span");
  subtitle.textContent = lead.organisation || lead.city || lead.project_name || "";
  body.appendChild(subtitle);

  const meta = document.createElement("small");
  meta.textContent = `${Number(lead.distance_km || 0).toFixed(2)} km - ${lead.status || ""}`;
  body.appendChild(meta);
  article.appendChild(body);

  const actions = document.createElement("div");
  actions.className = "mobile-nearby-lead__actions";

  if (lead.phone) {
    actions.appendChild(iconLink({
      className: "btn btn-sm btn--primary",
      href: safePhoneHref(lead.phone),
      iconClass: "fa-solid fa-phone",
      attributes: {
        "data-mobile-call-start": "",
        "data-lead-id": String(lead.id),
        "data-lead-name": lead.display_name || "Lead"
      }
    }));
  }

  if (lead.email) {
    actions.appendChild(iconLink({
      className: "btn btn-sm btn-outline-secondary",
      href: safeMailtoHref(lead.email),
      iconClass: "fa-regular fa-envelope"
    }));
  }

  actions.appendChild(iconLink({
    className: "btn btn-sm btn-outline-secondary",
    href: googleMapsRouteHref(lead.latitude, lead.longitude),
    iconClass: "fa-solid fa-route",
    target: "_blank",
    rel: "noopener"
  }));

  if (navigator.contacts && navigator.contacts.select) {
    const contactButton = document.createElement("button");
    contactButton.type = "button";
    contactButton.className = "btn btn-sm btn-outline-secondary";
    contactButton.dataset.mobileContactImport = "";
    contactButton.dataset.leadId = String(lead.id);
    contactButton.appendChild(icon("fa-regular fa-address-book"));
    actions.appendChild(contactButton);
  }

  article.appendChild(actions);
  return article;
}

function iconLink({ className, href, iconClass, target, rel, attributes = {} }) {
  const link = document.createElement("a");
  link.className = className;
  link.href = href;
  if (target) link.target = target;
  if (rel) link.rel = rel;

  Object.keys(attributes).forEach((name) => {
    link.setAttribute(name, attributes[name]);
  });

  link.appendChild(icon(iconClass));
  return link;
}

function icon(className) {
  const iconNode = document.createElement("i");
  iconNode.className = className;
  iconNode.setAttribute("aria-hidden", "true");
  return iconNode;
}

function safeInternalPath(path) {
  if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//")) return null;

  try {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (_error) {
    return null;
  }
}

function safePhoneHref(phone) {
  const value = String(phone || "").replace(/[^\d+().\-\s]/g, "").trim();
  return value ? `tel:${value}` : "#";
}

function safeMailtoHref(email) {
  const value = String(email || "").replace(/[\r\n]/g, "").trim();
  return value ? `mailto:${value}` : "#";
}

function googleMapsRouteHref(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "https://www.google.com/maps";

  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lat},${lng}`)}`;
}

function leadLabelFor(leadId) {
  const link = Array.from(document.querySelectorAll("[data-mobile-call-start]"))
    .find((node) => node.dataset.leadId === String(leadId));
  return (link && link.dataset.leadName) || "Lead";
}

function booleanMeta(name) {
  const node = document.querySelector(`meta[name="${name}"]`);
  return node && node.content === "true";
}

function isNativeIOSApp() {
  return mobilePlatform() === "ios" && booleanMeta("acvire-native-mobile-app");
}

function isSignedInNativeMobileSession() {
  return isNativeIOSApp() && booleanMeta("acvire-user-signed-in");
}

function deviceAuthPlugin() {
  const plugins = window.Capacitor && window.Capacitor.Plugins;
  return plugins && plugins.AcvireDeviceAuth;
}

function mobileAppVersion() {
  const node = document.querySelector("meta[name='acvire-app-version']");
  return node ? node.content : "";
}

function mobileDeviceName() {
  return (window.navigator && window.navigator.userAgent) ? window.navigator.userAgent.slice(0, 120) : "";
}

function mobileDeviceAuthReturnTo() {
  try {
    const returnTo = new URLSearchParams(window.location.search).get("return_to");
    return safeInternalPath(returnTo) || "/tasks?mobile_app=1";
  } catch (_error) {
    return "/tasks?mobile_app=1";
  }
}

function parseJsonResponse(response) {
  return response.json()
    .catch(() => ({}))
    .then((body) => ({ response, body }));
}

function createDeviceAuthCredential() {
  return postJson("/mobile/device_auth", {
    device_auth: {
      platform: mobilePlatform(),
      device_name: mobileDeviceName(),
      app_version: mobileAppVersion()
    }
  })
    .then(parseJsonResponse)
    .then(({ response, body }) => {
      if (!response.ok || !body.credential_id || !body.secret) {
        throw new Error(body.error || "Device auth credential could not be created");
      }

      return body;
    });
}

function revokeDeviceAuthCredential(credentialId) {
  if (!credentialId) return Promise.resolve();

  return fetch("/mobile/device_auth", {
    method: "DELETE",
    credentials: "same-origin",
    headers: csrfHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      device_auth: {
        credential_id: credentialId
      }
    })
  }).catch(() => {});
}

function enableDeviceAuth(plugin, reason) {
  let issuedCredential = null;

  return createDeviceAuthCredential()
    .then((credential) => {
      issuedCredential = credential;
      return plugin.enable({
        credentialId: credential.credential_id,
        secret: credential.secret,
        reason,
        cancelTitle: "Abbrechen"
      });
    })
    .catch((error) => {
      const credentialId = issuedCredential && issuedCredential.credential_id;
      return revokeDeviceAuthCredential(credentialId).then(() => {
        throw error;
      });
    });
}

function initializeDeviceAuthSignIn() {
  if (!isNativeIOSApp() || isSignedInNativeMobileSession()) return;

  const plugin = deviceAuthPlugin();
  if (!plugin || typeof plugin.status !== "function") return;

  plugin.status()
    .then((status) => {
      if (status && status.available && status.enabled) {
        showDeviceAuthSignIn(status.biometryType);
      }
    })
    .catch(() => {});
}

function showDeviceAuthSignIn(biometryType) {
  const panel = document.querySelector(".mobile-app-auth__panel");
  if (!panel || panel.querySelector("[data-mobile-device-auth-sign-in]")) return;

  const container = document.createElement("div");
  container.className = "mobile-app-auth__device-login";
  container.dataset.mobileDeviceAuthSignIn = "true";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "mobile-app-auth__oauth-button mobile-app-auth__device-button";
  button.appendChild(icon("fa-solid fa-fingerprint"));

  const label = document.createElement("span");
  label.textContent = `Mit ${deviceAuthLabel(biometryType)} einloggen`;
  button.appendChild(label);

  const message = document.createElement("small");
  message.dataset.mobileDeviceAuthSignInMessage = "";

  button.addEventListener("click", () => {
    signInWithDeviceAuth(button, message);
  });

  container.appendChild(button);
  container.appendChild(message);

  const tabs = panel.querySelector(".mobile-app-auth__tabs");
  panel.insertBefore(container, tabs || panel.firstElementChild);
}

function signInWithDeviceAuth(button, message) {
  const plugin = deviceAuthPlugin();
  if (!plugin || typeof plugin.authenticate !== "function") return;

  if (button) button.disabled = true;
  if (message) message.textContent = "Face ID wird geprueft ...";

  plugin.authenticate({
    reason: "Acvire mit Face ID einloggen.",
    cancelTitle: "Abbrechen"
  })
    .then((credential) => {
      if (!credential || !credential.credentialId || !credential.secret) {
        throw new Error("Stored credential missing");
      }

      return postJson("/mobile/device_auth/exchange", {
        device_auth: {
          credential_id: credential.credentialId,
          secret: credential.secret,
          platform: mobilePlatform(),
          return_to: mobileDeviceAuthReturnTo()
        }
      });
    })
    .then(parseJsonResponse)
    .then(({ response, body }) => {
      if (response.ok && body.ok) {
        navigateToPath(safeInternalPath(body.path) || "/tasks?mobile_app=1");
      } else {
        throw new Error(body.error || "Device auth login failed");
      }
    })
    .catch(() => {
      if (message) message.textContent = t("mobile.security.device_auth_sign_in_failed", {}, "Face ID Anmeldung fehlgeschlagen. Bitte erneut versuchen oder normal einloggen.");
    })
    .finally(() => {
      if (button) button.disabled = false;
    });
}

function mobilePinEnabled() {
  return Boolean(localStorage.getItem(MOBILE_PIN_HASH_KEY) && localStorage.getItem(MOBILE_PIN_SALT_KEY));
}

function randomPinSalt() {
  const bytes = new Uint8Array(16);
  if (window.crypto && typeof window.crypto.getRandomValues === "function") {
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  return `${Date.now()}-${Math.random()}`;
}

function hashMobilePin(pin, salt) {
  if (!window.crypto || !window.crypto.subtle || typeof TextEncoder === "undefined") {
    return Promise.reject(new Error("Web Crypto unavailable"));
  }

  const data = new TextEncoder().encode(`${salt}:${pin}`);
  return window.crypto.subtle.digest("SHA-256", data)
    .then((digest) => Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join(""));
}

function validMobilePinFormat(pin) {
  return /^[0-9]{4,8}$/.test(String(pin || ""));
}

function initializeSecuritySettings() {
  initializeDeviceAuthSettings();
  initializeMobilePinSettings();
}

function initializeDeviceAuthSettings() {
  const root = document.querySelector("[data-mobile-device-auth-settings]");
  if (!root || root.dataset.mobileDeviceAuthSettingsBound === "true") return;
  root.dataset.mobileDeviceAuthSettingsBound = "true";

  const statusNode = root.querySelector("[data-mobile-device-auth-settings-status]");
  const pill = root.querySelector("[data-mobile-device-auth-settings-pill]");
  const enableButton = root.querySelector("[data-mobile-device-auth-enable]");
  const disableButton = root.querySelector("[data-mobile-device-auth-disable]");
  const plugin = deviceAuthPlugin();

  const setUnavailable = () => {
    if (statusNode) statusNode.textContent = t("mobile.security.ios_only", {}, "Nur in der iOS-App verfügbar.");
    if (pill) {
      pill.textContent = t("common.off", {}, "Aus");
      pill.classList.remove("is-active");
    }
    [enableButton, disableButton].forEach((button) => {
      if (button) button.disabled = true;
    });
  };

  const render = (status) => {
    if (!status || !status.available) {
      setUnavailable();
      return;
    }

    const label = deviceAuthLabel(status.biometryType);
    if (statusNode) statusNode.textContent = status.enabled
      ? t("mobile.security.method_active", { label }, `${label} ist aktiv.`)
      : t("mobile.security.method_available", { label }, `${label} ist verfügbar.`);
    if (pill) {
      pill.textContent = status.enabled ? t("common.on", {}, "Ein") : t("common.off", {}, "Aus");
      pill.classList.toggle("is-active", Boolean(status.enabled));
    }
    if (enableButton) enableButton.disabled = Boolean(status.enabled);
    if (disableButton) disableButton.disabled = !status.enabled;
  };

  const refresh = () => {
    if (!isNativeIOSApp() || !plugin || typeof plugin.status !== "function") {
      setUnavailable();
      return;
    }

    plugin.status().then(render).catch(setUnavailable);
  };

  if (enableButton) {
    enableButton.addEventListener("click", () => {
      if (!plugin || typeof plugin.enable !== "function") return;
      enableButton.disabled = true;
      enableDeviceAuth(plugin, t("mobile.security.protect_login_reason", {}, "Acvire Anmeldung auf diesem iPhone schützen."))
        .then(() => {
          localStorage.removeItem(MOBILE_DEVICE_AUTH_PROMPT_DISMISSED_KEY);
          mobileDeviceAuthUnlockedAt = Date.now();
          mobileDeviceAuthSessionUnlocked = true;
          refresh();
        })
        .catch(refresh);
    });
  }

  if (disableButton) {
    disableButton.addEventListener("click", () => {
      if (!plugin || typeof plugin.disable !== "function") return;
      disableButton.disabled = true;
      plugin.status()
        .then((status) => revokeDeviceAuthCredential(status && status.credentialId))
        .catch(() => {})
        .then(() => plugin.disable())
        .then(() => {
          mobileDeviceAuthSessionUnlocked = false;
          refresh();
        })
        .catch(refresh);
    });
  }

  refresh();
}

function initializeMobilePinSettings() {
  const form = document.querySelector("[data-mobile-pin-form]");
  if (!form || form.dataset.mobilePinBound === "true") return;
  form.dataset.mobilePinBound = "true";

  const pinInput = form.querySelector("[data-mobile-pin]");
  const confirmInput = form.querySelector("[data-mobile-pin-confirm]");
  const disableButton = form.querySelector("[data-mobile-pin-disable]");
  const statusNode = document.querySelector("[data-mobile-pin-status]");
  const pill = document.querySelector("[data-mobile-pin-pill]");

  if (!isNativeIOSApp()) {
    if (statusNode) statusNode.textContent = t("mobile.security.ios_only", {}, "Nur in der iOS-App verfügbar.");
    if (pill) {
      pill.textContent = t("common.off", {}, "Aus");
      pill.classList.remove("is-active");
    }
    [pinInput, confirmInput, disableButton, form.querySelector("button[type='submit']")].forEach((node) => {
      if (node) node.disabled = true;
    });
    return;
  }

  const render = (message) => {
    const enabled = mobilePinEnabled();
    if (statusNode) statusNode.textContent = message || (enabled ? t("mobile.security.pin_active", {}, "PIN ist auf diesem Gerät aktiv.") : t("mobile.security.local_only", {}, "Nur auf diesem Gerät gespeichert."));
    if (pill) {
      pill.textContent = enabled ? t("common.on", {}, "Ein") : t("common.off", {}, "Aus");
      pill.classList.toggle("is-active", enabled);
    }
    if (disableButton) disableButton.disabled = !enabled;
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const pin = pinInput ? pinInput.value : "";
    const confirmation = confirmInput ? confirmInput.value : "";

    if (!validMobilePinFormat(pin)) {
      render(t("mobile.security.pin_format", {}, "Bitte 4 bis 8 Ziffern eingeben."));
      return;
    }

    if (pin !== confirmation) {
      render(t("mobile.security.pin_mismatch", {}, "Die PINs stimmen nicht überein."));
      return;
    }

    const salt = randomPinSalt();
    hashMobilePin(pin, salt)
      .then((hash) => {
        localStorage.setItem(MOBILE_PIN_SALT_KEY, salt);
        localStorage.setItem(MOBILE_PIN_HASH_KEY, hash);
        mobilePinSessionUnlocked = true;
        if (pinInput) pinInput.value = "";
        if (confirmInput) confirmInput.value = "";
        render(t("mobile.security.pin_active", {}, "PIN ist auf diesem Gerät aktiv."));
      })
      .catch(() => render(t("mobile.security.pin_save_failed", {}, "PIN konnte auf diesem Gerät nicht gespeichert werden.")));
  });

  if (disableButton) {
    disableButton.addEventListener("click", () => {
      localStorage.removeItem(MOBILE_PIN_SALT_KEY);
      localStorage.removeItem(MOBILE_PIN_HASH_KEY);
      mobilePinSessionUnlocked = false;
      render(t("mobile.security.pin_removed", {}, "PIN wurde entfernt."));
    });
  }

  render();
}

function initializeDeviceLoginProtection() {
  if (!isNativeIOSApp()) return;

  const plugin = deviceAuthPlugin();

  bindDeviceAuthAppState();

  if (!isSignedInNativeMobileSession()) {
    initializeDeviceAuthSignIn();
    return;
  }

  if (!plugin || typeof plugin.status !== "function") {
    if (mobilePinEnabled()) authenticateMobilePin(t("mobile.security.unlock_saved_login", {}, "Acvire entsperren, um deine gespeicherte Anmeldung zu nutzen."));
    return;
  }

  plugin.status()
    .then((status) => {
      if (!status || !status.available) {
        if (mobilePinEnabled()) authenticateMobilePin(t("mobile.security.unlock_saved_login", {}, "Acvire entsperren, um deine gespeicherte Anmeldung zu nutzen."));
        return;
      }

      if (status.enabled) {
        authenticateDeviceLogin(t("mobile.security.unlock_saved_login", {}, "Acvire entsperren, um deine gespeicherte Anmeldung zu nutzen."));
      } else if (mobilePinEnabled()) {
        authenticateMobilePin(t("mobile.security.unlock_saved_login", {}, "Acvire entsperren, um deine gespeicherte Anmeldung zu nutzen."));
      } else {
        showDeviceAuthEnrollment(status.biometryType);
      }
    })
    .catch(() => {});
}

function bindDeviceAuthAppState() {
  if (window.__acvireDeviceAuthAppStateBound) return;

  const plugins = window.Capacitor && window.Capacitor.Plugins;
  const app = plugins && plugins.App;
  if (!app || typeof app.addListener !== "function") return;

  window.__acvireDeviceAuthAppStateBound = true;
  app.addListener("appStateChange", (state) => {
    if (!state) return;

    if (state.isActive) {
      const elapsed = Date.now() - mobileDeviceAuthBackgroundedAt;
      if (mobileDeviceAuthBackgroundedAt && elapsed >= MOBILE_DEVICE_AUTH_BACKGROUND_LOCK_MS) {
        authenticatePreferredMobileLock("Acvire erneut entsperren.");
      }
      mobileDeviceAuthBackgroundedAt = 0;
    } else {
      mobileDeviceAuthBackgroundedAt = Date.now();
      mobileDeviceAuthUnlockedAt = 0;
      mobileDeviceAuthSessionUnlocked = false;
      mobilePinSessionUnlocked = false;
    }
  });
}

function authenticatePreferredMobileLock(reason) {
  if (!isSignedInNativeMobileSession()) return;

  const plugin = deviceAuthPlugin();
  if (!plugin || typeof plugin.status !== "function") {
    if (mobilePinEnabled()) authenticateMobilePin(reason);
    return;
  }

  plugin.status()
    .then((status) => {
      if (status && status.available && status.enabled) {
        authenticateDeviceLogin(reason);
      } else if (mobilePinEnabled()) {
        authenticateMobilePin(reason);
      }
    })
    .catch(() => {
      if (mobilePinEnabled()) authenticateMobilePin(reason);
    });
}

function authenticateDeviceLogin(reason) {
  if (!isSignedInNativeMobileSession()) return;
  if (mobileDeviceAuthAuthenticating) return;
  if (mobileDeviceAuthSessionUnlocked) return;
  if (mobileDeviceAuthUnlockedAt && Date.now() - mobileDeviceAuthUnlockedAt < 1000) return;

  const plugin = deviceAuthPlugin();
  if (!plugin || typeof plugin.authenticate !== "function") return;

  mobileDeviceAuthAuthenticating = true;
  showDeviceAuthLock("locked");

  plugin.authenticate({
    reason,
    cancelTitle: "Abbrechen"
  })
    .then(() => {
      mobileDeviceAuthUnlockedAt = Date.now();
      mobileDeviceAuthSessionUnlocked = true;
      hideDeviceAuthLock();
    })
    .catch(() => {
      showDeviceAuthLock("failed");
    })
    .finally(() => {
      mobileDeviceAuthAuthenticating = false;
    });
}

function authenticateMobilePin(reason) {
  if (!isSignedInNativeMobileSession()) return;
  if (!mobilePinEnabled()) return;
  if (mobilePinSessionUnlocked) return;

  showMobilePinLock(reason);
}

function showMobilePinLock(reason, state = "locked") {
  let lock = document.querySelector("[data-mobile-pin-lock]");

  if (!lock) {
    lock = document.createElement("div");
    lock.className = "mobile-device-auth-lock";
    lock.dataset.mobilePinLock = "true";
    lock.innerHTML = `
      <div class="mobile-device-auth-lock__panel" role="dialog" aria-modal="true" aria-labelledby="mobilePinAuthTitle">
        <span class="mobile-device-auth-lock__icon"><i class="fa-solid fa-key"></i></span>
        <h2 id="mobilePinAuthTitle">${t("mobile.security.lock_title", {}, "Acvire ist geschützt")}</h2>
        <p data-mobile-pin-message></p>
        <form data-mobile-pin-unlock-form>
          <input class="mobile-device-auth-lock__pin" type="password" inputmode="numeric" autocomplete="one-time-code" maxlength="8" pattern="[0-9]*" aria-label="${t("mobile.security.pin_aria", {}, "App-PIN")}" data-mobile-pin-unlock>
          <button type="submit" class="btn btn--primary">${t("common.unlock", {}, "Entsperren")}</button>
        </form>
        <a href="/users/sign_out" data-method="delete" rel="nofollow">${t("common.logout", {}, "Abmelden")}</a>
      </div>
    `;
    document.body.appendChild(lock);

    const form = lock.querySelector("[data-mobile-pin-unlock-form]");
    const input = lock.querySelector("[data-mobile-pin-unlock]");
    if (form) {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        verifyMobilePinUnlock(input);
      });
    }
  }

  const message = lock.querySelector("[data-mobile-pin-message]");
  if (message) {
    message.textContent = state === "failed" ? t("mobile.security.pin_wrong", {}, "Die PIN ist falsch.") : (reason || t("mobile.security.enter_pin", {}, "Bitte gib deine App-PIN ein."));
  }

  lock.hidden = false;
  document.body.classList.add("mobile-device-auth-is-locked");

  const input = lock.querySelector("[data-mobile-pin-unlock]");
  if (input) {
    input.value = "";
    setTimeout(() => input.focus(), 50);
  }
}

function verifyMobilePinUnlock(input) {
  const pin = input ? input.value : "";
  const salt = localStorage.getItem(MOBILE_PIN_SALT_KEY);
  const expectedHash = localStorage.getItem(MOBILE_PIN_HASH_KEY);

  if (!validMobilePinFormat(pin) || !salt || !expectedHash) {
    showMobilePinLock(null, "failed");
    return;
  }

  hashMobilePin(pin, salt)
    .then((hash) => {
      if (hash === expectedHash) {
        mobilePinSessionUnlocked = true;
        hideMobilePinLock();
      } else {
        showMobilePinLock(null, "failed");
      }
    })
    .catch(() => showMobilePinLock(null, "failed"));
}

function hideMobilePinLock() {
  const lock = document.querySelector("[data-mobile-pin-lock]");
  if (lock) lock.hidden = true;
  document.body.classList.remove("mobile-device-auth-is-locked");
}

function showDeviceAuthEnrollment(biometryType) {
  if (document.querySelector("[data-mobile-device-auth-enroll]")) return;

  const dismissedAt = Number(localStorage.getItem(MOBILE_DEVICE_AUTH_PROMPT_DISMISSED_KEY) || 0);
  if (dismissedAt && Date.now() - dismissedAt < MOBILE_DEVICE_AUTH_PROMPT_COOLDOWN_MS) return;

  const plugin = deviceAuthPlugin();
  if (!plugin || typeof plugin.enable !== "function") return;

  const prompt = document.createElement("aside");
  prompt.className = "mobile-device-auth-enroll";
  prompt.dataset.mobileDeviceAuthEnroll = "true";

  const copy = document.createElement("div");
  const title = document.createElement("strong");
  title.textContent = t("mobile.security.activate_method", { label: deviceAuthLabel(biometryType) }, `${deviceAuthLabel(biometryType)} aktivieren`);
  const text = document.createElement("span");
  text.textContent = t("mobile.security.protect_login_text", {}, "Schütze deine gespeicherte Anmeldung auf diesem iPhone.");
  copy.appendChild(title);
  copy.appendChild(text);
  prompt.appendChild(copy);

  const actions = document.createElement("div");
  const enableButton = document.createElement("button");
  enableButton.type = "button";
  enableButton.className = "btn btn--primary btn-sm";
  enableButton.textContent = t("common.activate", {}, "Aktivieren");
  const laterButton = document.createElement("button");
  laterButton.type = "button";
  laterButton.className = "btn btn-sm btn-outline-secondary";
  laterButton.textContent = t("common.later", {}, "Später");
  actions.appendChild(laterButton);
  actions.appendChild(enableButton);
  prompt.appendChild(actions);

  enableButton.addEventListener("click", () => {
    enableButton.disabled = true;
    enableDeviceAuth(plugin, t("mobile.security.protect_login_reason", {}, "Acvire Anmeldung auf diesem iPhone schützen."))
      .then(() => {
        localStorage.removeItem(MOBILE_DEVICE_AUTH_PROMPT_DISMISSED_KEY);
        mobileDeviceAuthUnlockedAt = Date.now();
        mobileDeviceAuthSessionUnlocked = true;
        prompt.remove();
      })
      .catch(() => {
        enableButton.disabled = false;
      });
  });

  laterButton.addEventListener("click", () => {
    localStorage.setItem(MOBILE_DEVICE_AUTH_PROMPT_DISMISSED_KEY, String(Date.now()));
    prompt.remove();
  });

  document.body.appendChild(prompt);
}

function deviceAuthLabel(biometryType) {
  if (biometryType === "faceId") return "Face ID";
  if (biometryType === "touchId") return "Touch ID";
  return t("mobile.security.device_passcode", {}, "Gerätecode");
}

function showDeviceAuthLock(state) {
  let lock = document.querySelector("[data-mobile-device-auth-lock]");

  if (!lock) {
    lock = document.createElement("div");
    lock.className = "mobile-device-auth-lock";
    lock.dataset.mobileDeviceAuthLock = "true";
    lock.innerHTML = `
      <div class="mobile-device-auth-lock__panel" role="dialog" aria-modal="true" aria-labelledby="mobileDeviceAuthTitle">
        <span class="mobile-device-auth-lock__icon"><i class="fa-solid fa-lock"></i></span>
        <h2 id="mobileDeviceAuthTitle">${t("mobile.security.lock_title", {}, "Acvire ist geschützt")}</h2>
        <p data-mobile-device-auth-message>${t("mobile.security.confirm_device_auth", {}, "Bitte bestätige dich mit Face ID oder Gerätecode.")}</p>
        <button type="button" class="btn btn--primary" data-mobile-device-auth-retry>${t("common.unlock", {}, "Entsperren")}</button>
        <a href="/users/sign_out" data-method="delete" rel="nofollow" data-mobile-device-auth-logout>${t("common.logout", {}, "Abmelden")}</a>
      </div>
    `;
    document.body.appendChild(lock);

    const retry = lock.querySelector("[data-mobile-device-auth-retry]");
    if (retry) {
      retry.addEventListener("click", () => {
        authenticateDeviceLogin(t("mobile.security.unlock_acvire", {}, "Acvire entsperren."));
      });
    }

  }

  const message = lock.querySelector("[data-mobile-device-auth-message]");
  if (message) {
    message.textContent = state === "failed"
      ? t("mobile.security.unlock_cancelled", {}, "Entsperren wurde abgebrochen. Versuche es erneut oder melde dich ab.")
      : t("mobile.security.confirm_device_auth", {}, "Bitte bestätige dich mit Face ID oder Gerätecode.");
  }

  lock.hidden = false;
  document.body.classList.add("mobile-device-auth-is-locked");
}

function hideDeviceAuthLock() {
  const lock = document.querySelector("[data-mobile-device-auth-lock]");
  if (lock) lock.hidden = true;
  document.body.classList.remove("mobile-device-auth-is-locked");
}

function initializeMobileApp() {
  initializeNativeBridge();
  initializeMobileOauth();
  initializeNativeSignedInAuthGuards();
  initializeMobileAuthTabs();
  initializeSecuritySettings();
  initializeDeviceLoginProtection();
  initializeNearbyLeads();
  bindCallActions();
  bindContactImport();
  initializeCallLogForm();
  initializeAutomationJsonActions();
  maybeShowCallLogPrompt();
}

document.addEventListener("turbolinks:load", initializeMobileApp);
document.addEventListener("DOMContentLoaded", initializeMobileApp);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") maybeShowCallLogPrompt();
});
window.addEventListener("focus", maybeShowCallLogPrompt);
